import { useMediaQuery, useTheme } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';

import { Switch } from '@/components/common/Switch';
import { AddPropertyModal } from '@/components/dashboard/AddPropertyModal';
import { NeighborhoodMapCard } from '@/components/dashboard/scoring/NeighborhoodMapCard';
import { ScoringStatusChips } from '@/components/dashboard/scoring/ScoringStatusChips';
import {
  Button as DsButton,
  CloseButton,
  IconButton as DsIconButton,
  Segmented,
  SortCaret,
  TextInput,
} from '@/components/ds';
import { DealPickerModal } from '@/components/property/DealPickerModal';
import { UploadFile, UploadReportModal } from '@/components/property/UploadReportModal';
import { AddressMismatchDialog } from '@/components/uploader/AddressMismatchDialog';
import { usePropertiesContext } from '@/context/PropertiesProvider';
import { useSnackbar } from '@/context/SnackbarProvider';
import { useUploaderContext } from '@/context/UploaderProvider';
import {
  ADMIN_PROPERTIES_QUERY_KEY,
  useAdminProperties,
  useAdminPropertiesSearch,
  useDeleteProperty,
  useUpdateHasHOA,
} from '@/hooks/admin/useAdminProperties';
import { useHauserEQResults } from '@/hooks/hauser-eq';
import { CLIENT_GROUPS_QUERY_KEY, useClientGroups } from '@/hooks/useClientGroups';
import { useDocumentRecords } from '@/hooks/useDocumentRecords';
import { useUserRole } from '@/hooks/useUserRole';
import {
  AddressCheckStatus,
  DocumentClassificationType,
  DOCUMENT_TYPE_CONFIG,
  DOCUMENT_TYPE_LABELS,
} from '@/models/DocumentRecord';
import { isApiError } from '@/services/api';
import { AdminProperty, adminPropertyToUserProperty } from '@/services/api/admin';
import { createListing } from '@/services/api/clientGroups';
import {
  deleteDocumentRecord,
  generateDocumentRecordUrl,
} from '@/services/api/document-records/api';
import {
  cancelPendingUpload,
  confirmAddressOverride,
  createDocuments,
  createDocumentUploadBatch,
  recordDocumentUploadBatchFailure,
} from '@/services/api/documents/api';
import { generatePreSignedUrls } from '@/services/api/files/api';
import { useTranslationPrefix } from '@/translations/i18n';
import { ClientGroup as FullClientGroup } from '@/types/clientGroups';
import { epochSecondsToLocalDateStr, formatLocalDate } from '@/utils/formatDate';
import { openInNewTab } from '@/utils/openInNewTab';
import {
  buildSellerDisclosureDisplayRecords,
  getCarFormAcronym,
  looksLikeRealAcronym,
  withPdfPage,
} from '@/utils/sellerDisclosureDocuments';

const C = {
  bg: 'var(--tx-fill-2)',
  card: 'var(--tx-glass-panel)',
  input: 'var(--tx-fill)',
  border: 'var(--tx-line)',
  borderLight: 'var(--tx-line)',
  text: 'var(--tx-text)',
  muted: 'var(--tx-muted)',
  subtle: 'var(--tx-faint)',
  lime: '#c0ff02',
  danger: '#ff4d4d',
};
const L = {
  critical: '#B05670',
  urgent: '#E68A6B',
  moderate: '#EBC064',
  low: '#6BB1B1',
  monitor: 'var(--tx-faint)',
  resolved: '#C0FF02',
};
const font = "'Manrope', sans-serif";

type PropertyDocumentGroupId =
  | 'inspection'
  | 'seller-disclosures'
  | 'hoa'
  | 'other'
  | 'title-escrow';

const MG_FULL =
  '28px minmax(160px,2.2fr) minmax(100px,1fr) minmax(85px,1fr) minmax(110px,1.2fr) minmax(50px,1fr) minmax(50px,.7fr) minmax(50px,1fr) 136px';
const MG_COMPACT =
  '28px minmax(160px,2.2fr) minmax(100px,1fr) minmax(85px,1fr) minmax(110px,1.2fr) minmax(50px,1fr) minmax(50px,.7fr) minmax(50px,1fr) 40px';
const insetDivider = {
  backgroundImage: `linear-gradient(#3a3a3a,#3a3a3a)`,
  backgroundRepeat: 'no-repeat',
  backgroundSize: 'calc(100% - 40px) 1px',
  backgroundPosition: '20px bottom',
};
const btnGhost: React.CSSProperties = {
  height: 40,
  padding: '0 24px',
  fontWeight: 600,
  background: 'var(--tx-fill)',
  color: C.muted,
  borderRadius: 10,
  border: 0,
  cursor: 'pointer',
  fontFamily: font,
  fontSize: 14,
  letterSpacing: '-0.02em',
};

interface ClientGroup {
  id: string;
  name: string;
  owner: string;
  userId: string;
}

interface NormalizedProperty {
  id: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  createdAt: string;
  assignedGroups: string[];
  hasReport: boolean;
  inspectionReportCount: number;
  interestCount: number;
  price?: number;
  listedDate?: number;
  lat?: number;
  lng?: number;
  /** Cross-group deal status from another advisor's group ('Under Contract' | 'Pending' | 'Closed'). */
  dealStatus?: string;
  /** Whether this property is HOA-governed. Manually toggled, or set automatically
   *  the first time a document classifies as HOA_DEMAND for this property. */
  hasHOA?: boolean;
  /** HauserEQ scoring-pipeline status — see ScoringStatusChips. */
  hauserEq?: AdminProperty['hauserEq'];
}

// Shared by both the full-fetch (KPI totals, buyer-view enrichment) and the
// paginated/searched (admin/advisor list) property sources — same AdminProperty
// shape either way. Address/city/zip/state are typed `string` upstream but a
// legacy Property row without an address surfaces here as `undefined`,
// crashing the sort/filter below (`streetName` calls `.replace` on the
// value). Default to '' at this boundary so the rest of the page can rely
// on the NormalizedProperty type.
function mapAdminProperty(
  p: AdminProperty,
  groupIdsByProperty: Record<string, string[]>,
): NormalizedProperty {
  return {
    id: p.id,
    address: p.address ?? '',
    city: p.city ?? '',
    state: p.state ?? '',
    zip: p.zipCode ?? '',
    createdAt: epochSecondsToLocalDateStr(p.createdAt),
    assignedGroups: groupIdsByProperty[p.id] ?? [],
    hasReport: (p.inspectionReportCount ?? 0) > 0,
    inspectionReportCount: p.inspectionReportCount ?? 0,
    interestCount: p.interestCount ?? 0,
    price: p.price,
    listedDate: p.listedDate,
    lat: p.latitude,
    lng: p.longitude,
    dealStatus: p.dealStatus,
    hasHOA: p.hasHOA,
    hauserEq: p.hauserEq,
  };
}

function Tooltip({ label, children }: { label: string; children: React.ReactNode }) {
  const [visible, setVisible] = useState(false);
  return (
    <div
      style={{ position: 'relative', display: 'inline-flex' }}
      onMouseEnter={() => setVisible(true)}
      onMouseLeave={() => setVisible(false)}>
      {children}
      {visible && (
        <div
          style={{
            position: 'absolute',
            bottom: 'calc(100% + 6px)',
            right: 0,
            background: 'var(--tx-glass)',
            boxShadow: 'var(--tx-shadow-sm)',
            backdropFilter: 'var(--tx-blur)',
            WebkitBackdropFilter: 'var(--tx-blur)',
            color: 'var(--tx-text)',
            fontSize: 12,
            padding: 10,
            borderRadius: '12px',
            whiteSpace: 'nowrap',
            pointerEvents: 'none',
            zIndex: 100,
            fontFamily: font,
            fontWeight: 500,
          }}>
          {label}
        </div>
      )}
    </div>
  );
}

function AvatarBadge({ name, size = 28 }: { name: string; size?: number }) {
  const initials = name
    .split(' ')
    .map((w: string) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        flexShrink: 0,
        background: 'var(--tx-fill)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: size * 0.36,
        fontWeight: 600,
        color: C.muted,
        fontFamily: font,
      }}>
      {initials}
    </div>
  );
}

function ReportBadge({
  hasReport,
  count,
  mobileSize,
}: {
  hasReport: boolean;
  count: number;
  mobileSize?: number;
}) {
  const sz = mobileSize ?? 13;
  if (!hasReport) return <span style={{ fontSize: sz, color: C.subtle, fontFamily: font }}>—</span>;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <svg
        width={14}
        height={14}
        viewBox="0 0 24 24"
        fill="none"
        stroke="var(--tx-muted)"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{ flexShrink: 0 }}>
        <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
        <polyline points="14 2 14 8 20 8" />
      </svg>
      <span style={{ fontSize: sz, fontWeight: 500, color: 'var(--tx-text)', fontFamily: font }}>
        {count}
      </span>
    </div>
  );
}

function ClientChip({ group }: { group: ClientGroup }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        height: 24,
        background: 'var(--tx-fill-2)',
        borderRadius: 6,
        padding: '0 8px 0 4px',
      }}>
      <AvatarBadge name={group.name} size={16} />
      <span
        style={{
          fontSize: 11,
          fontWeight: 600,
          color: C.text,
          fontFamily: font,
          whiteSpace: 'nowrap',
        }}>
        {group.name}
      </span>
    </div>
  );
}

// The portfolio at a glance: five widget cards in the Clients & Deals row's grammar, each a label, a light figure and
// a hairline visual drawn from the properties themselves
function KpiBar({ properties }: { properties: NormalizedProperty[] }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const total = properties.length;
  const assigned = properties.filter((p) => p.assignedGroups.length > 0).length;
  const withReports = properties.filter((p) => p.hasReport || p.inspectionReportCount > 0).length;
  const documentsOnFile = properties.reduce((count, property) => count + property.inspectionReportCount, 0);
  const docCounts = properties.map((p) => p.inspectionReportCount).sort((a, b) => b - a);
  const prices = properties
    .map((p) => p.price)
    .filter((v): v is number => typeof v === 'number' && v > 0)
    .sort((a, b) => a - b);
  const median = prices.length ? prices[Math.floor(prices.length / 2)] : null;
  const cityCounts = Object.entries(
    properties.reduce<Record<string, number>>((acc, p) => {
      if (p.city) acc[p.city] = (acc[p.city] ?? 0) + 1;
      return acc;
    }, {}),
  ).sort((a, b) => b[1] - a[1]);
  const money = (v: number) =>
    v >= 1_000_000 ? `$${(v / 1_000_000).toFixed(v >= 10_000_000 ? 0 : 1)}M` : `$${Math.round(v / 1000)}K`;

  const card: React.CSSProperties = {
    background: C.card,
    backgroundImage: 'var(--tx-surface-light)',
    boxShadow: 'var(--tx-shadow)',
    borderRadius: '24px',
    padding: 20,
    minHeight: 150,
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
    minWidth: 0,
  };
  // value first, its label beneath (Tradehaus .tx-vz-card)
  const label = (text: string) => (
    <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--tx-muted)', fontFamily: font, marginTop: -8 }}>{text}</div>
  );
  const figure = (value: React.ReactNode, unit?: string) => (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, fontFamily: font }}>
      <span style={{ fontSize: 40, fontWeight: 200, letterSpacing: '-0.05em', lineHeight: 1, color: 'var(--tx-text)' }}>
        {value}
      </span>
      {unit && <span style={{ fontSize: 11, fontWeight: 500, color: 'var(--tx-muted)' }}>{unit}</span>}
    </div>
  );
  const caption = (left: string, right: string) => (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        fontSize: 11,
        fontWeight: 500,
        color: 'var(--tx-muted)',
        fontFamily: font,
      }}>
      <span>{left}</span>
      <span>{right}</span>
    </div>
  );
  const ticks = (values: number[]) => {
    const max = Math.max(1, ...values);
    return (
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height: 28, borderBottom: '1px solid var(--tx-line)' }}>
        {values.map((v, i) => (
          <span
            key={i}
            style={{
              width: 2,
              height: v ? `${Math.max(10, (v / max) * 100)}%` : 2,
              borderRadius: '1px 1px 0 0',
              background: v ? 'var(--tx-muted)' : 'var(--tx-fill-3)',
            }}
          />
        ))}
      </div>
    );
  };
  const foot = (children: React.ReactNode) => (
    <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: 8 }}>{children}</div>
  );

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(5, minmax(0, 1fr))',
        gap: isMobile ? 10 : 20,
        marginBottom: isMobile ? 10 : 20,
      }}>
      <div style={card}>
        {figure(total)}
        {label('Properties')}
        {foot(
          <>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
              {properties.map((p) => (
                <span
                  key={p.id}
                  title={p.address}
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    background: p.assignedGroups.length ? 'var(--tx-text)' : 'var(--tx-fill-3)',
                  }}
                />
              ))}
            </div>
            {caption(`${assigned} assigned`, `${total - assigned} unassigned`)}
          </>,
        )}
      </div>
      <div style={card}>
        {figure(withReports, `of ${total}`)}
        {label('Reports on file')}
        {foot(
          <>
            <div style={{ display: 'flex', gap: 2, height: 4, borderRadius: 2, overflow: 'hidden' }}>
              {withReports > 0 && <div style={{ flex: withReports, background: 'var(--tx-text)' }} />}
              {total - withReports > 0 && <div style={{ flex: total - withReports, background: 'var(--tx-fill-2)' }} />}
            </div>
            {caption('With reports', 'Not yet')}
          </>,
        )}
      </div>
      <div style={card}>
        {figure(documentsOnFile)}
        {label('Documents')}
        {foot(
          <>
            {ticks(docCounts)}
            {caption('Most', 'Fewest')}
          </>,
        )}
      </div>
      <div style={card}>
        {figure(median ? money(median) : '—', median ? 'median' : undefined)}
        {label('List prices')}
        {foot(
          <>
            {ticks(prices)}
            {prices.length > 0 && caption(money(prices[0]), money(prices[prices.length - 1]))}
          </>,
        )}
      </div>
      {/* the fifth tile takes a phone's whole last row rather than leaving a hole beside it */}
      <div style={isMobile ? { ...card, gridColumn: '1 / -1' } : card}>
        {figure(cityCounts.length, cityCounts.length === 1 ? 'city' : 'cities')}
        {label('Markets')}
        {foot(
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {cityCounts.slice(0, 3).map(([city, count]) => (
              <div key={city} style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 64px 16px', alignItems: 'center', gap: 8, fontFamily: font }}>
                <span style={{ fontSize: 11, fontWeight: 500, color: 'var(--tx-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {city}
                </span>
                <span style={{ height: 2, borderRadius: 1, background: 'var(--tx-fill-2)', position: 'relative' }}>
                  <span
                    style={{
                      position: 'absolute',
                      inset: '0 auto 0 0',
                      width: `${(count / cityCounts[0][1]) * 100}%`,
                      borderRadius: 1,
                      background: 'var(--tx-text)',
                    }}
                  />
                </span>
                <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--tx-text)', textAlign: 'right' }}>{count}</span>
              </div>
            ))}
          </div>,
        )}
      </div>
    </div>
  );
}

function PropertyMapModal({
  property,
  onClose,
}: {
  property: NormalizedProperty;
  onClose: () => void;
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { nearestPointsOfInterest, isLoading: isEqLoading } = useHauserEQResults(property.id);
  return createPortal(
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'var(--tx-backdrop)',
        display: 'flex',
        alignItems: isMobile ? 'flex-end' : 'center',
        justifyContent: 'center',
        zIndex: 1400,
        padding: isMobile ? 0 : 24,
      }}
      onClick={onClose}>
      <div
        style={{
          width: '100%',
          maxWidth: isMobile ? '100%' : 900,
          overflow: 'hidden',
          position: 'relative',
        }}
        onClick={(e) => e.stopPropagation()}>
        <CloseButton
          onClose={onClose}
          label="Close"
          sx={{ position: 'absolute', top: 18, right: 18, zIndex: 2 }}
        />
        {property.lat && property.lng ? (
          <NeighborhoodMapCard
            center={{ latitude: property.lat, longitude: property.lng }}
            propertyAddress={property.address}
            isLoading={isEqLoading}
            nearestPointsOfInterest={nearestPointsOfInterest}
          />
        ) : (
          <div
            style={{
              padding: '24px',
              fontSize: 13,
              color: C.subtle,
              fontFamily: font,
              fontStyle: 'italic',
            }}>
            No coordinates available for this property.
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

const UC_LOCK_COLOR = '#FFF981';

function UnderContractLockChip() {
  return (
    <span
      style={{
        fontSize: 11,
        fontWeight: 600,
        fontFamily: font,
        color: UC_LOCK_COLOR,
        background: `${UC_LOCK_COLOR}18`,
        border: 0,
        borderRadius: 6,
        padding: '4px 8px',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        animation: 'uc-glow-pulse 2s ease-in-out infinite',
      }}>
      <svg
        width="11"
        height="11"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{ flexShrink: 0 }}>
        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
      </svg>
      Under Contract
    </span>
  );
}

function DealStatusChip({ status }: { status: string }) {
  const isPending = status === 'Pending' || status === 'Escrow';
  const color = isPending ? '#5AE9FF' : '#C0FF02';
  const label = status === 'Escrow' ? 'Pending' : status;
  return (
    <span
      style={{
        fontSize: 11,
        fontWeight: 600,
        fontFamily: font,
        color,
        background: `${color}18`,
        border: 0,
        borderRadius: 6,
        padding: '4px 8px',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
      }}>
      {label}
    </span>
  );
}

function PropertyRow({
  property,
  clientGroups,
  rawGroups,
  isOpen,
  isLast,
  onToggle,
  onUpload,
  onDelete,
  onMapOpen,
  onOpen,
  isAdmin,
}: {
  property: NormalizedProperty;
  clientGroups: ClientGroup[];
  rawGroups: FullClientGroup[];
  isOpen: boolean;
  isLast: boolean;
  onToggle: () => void;
  onUpload: (p: NormalizedProperty) => void;
  onDelete: (id: string) => void;
  isAdmin?: boolean;
  onMapOpen: (p: NormalizedProperty) => void;
  onOpen: (id: string) => void;
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isCompact = useMediaQuery(theme.breakpoints.down('lg'));
  const queryClient = useQueryClient();
  const { showAlert } = useSnackbar();
  const t = useTranslationPrefix('toasts');
  const { isAdvisor } = useUserRole();
  const canManageAccess = isAdmin || isAdvisor;
  const updateHasHOAMutation = useUpdateHasHOA();
  const assigned = clientGroups.filter((g) => property.assignedGroups.includes(g.id));
  const interestCount = property.interestCount;
  // Fetch documents only when expanded
  const { documentRecords } = useDocumentRecords(isOpen ? property.id : '');
  const docRecords = (documentRecords || []).filter((r: any) => r.documentId);
  const classifiedDocumentRecords = docRecords.filter(
    (r: any) => r.documentType && r.documentType !== DocumentClassificationType.PENDING,
  );
  const sellerDisclosureDisplayRecords =
    buildSellerDisclosureDisplayRecords(classifiedDocumentRecords);
  const [activeDocumentGroup, setActiveDocumentGroup] = useState<'all' | PropertyDocumentGroupId>(
    'all',
  );
  const propertyDocumentGroups = [
    {
      id: 'inspection',
      label: 'Inspection reports',
      records: classifiedDocumentRecords.filter(
        (r: any) =>
          DOCUMENT_TYPE_CONFIG[r.documentType as DocumentClassificationType]?.group ===
          'inspection',
      ),
    },
    {
      id: 'seller-disclosures',
      label: 'Seller disclosures',
      records: sellerDisclosureDisplayRecords,
    },
    {
      id: 'hoa',
      label: 'HOA',
      records: classifiedDocumentRecords.filter(
        (r: any) =>
          DOCUMENT_TYPE_CONFIG[r.documentType as DocumentClassificationType]?.group === 'hoa',
      ),
    },
    {
      id: 'other',
      label: 'Other documents',
      records: classifiedDocumentRecords.filter((r: any) => {
        const group = DOCUMENT_TYPE_CONFIG[r.documentType as DocumentClassificationType]?.group;
        return group !== 'hoa' && group !== 'inspection' && group !== 'seller-disclosure';
      }),
    },
    {
      id: 'title-escrow',
      label: 'Title & escrow',
      records: [],
    },
  ] as const;
  const visibleDocumentGroups =
    activeDocumentGroup === 'all'
      ? propertyDocumentGroups
      : propertyDocumentGroups.filter((group) => group.id === activeDocumentGroup);
  const documentTabs = [
    {
      id: 'all' as const,
      label: 'All',
      count: propertyDocumentGroups.reduce((count, group) => count + group.records.length, 0),
    },
    ...propertyDocumentGroups.map((group) => ({
      id: group.id,
      label: group.label,
      count: group.records.length,
    })),
  ];

  // Address-mismatch retry: clicking a yellow "Pending" chip opens the
  // AddressMismatchDialog (same as admin) so the user can view the document,
  // review the LLM-extracted address vs the property address, and confirm
  // the override checkbox before re-running the pipeline.
  const [retryTargetRecord, setRetryTargetRecord] = useState<{
    record: any;
    property: NormalizedProperty;
  } | null>(null);
  const [retrySubmitting, setRetrySubmitting] = useState(false);
  const [retryError, setRetryError] = useState<string | undefined>(undefined);

  const handleConfirmRetry = async () => {
    const r = retryTargetRecord?.record;
    if (!r?.documentId) {
      setRetryError('This record is not linked to a document, so it cannot be re-run.');
      return;
    }
    setRetrySubmitting(true);
    setRetryError(undefined);
    try {
      await confirmAddressOverride(r.documentId);
      showAlert(t('report.uploaded') ?? 'Re-running with override.', 'success');
      queryClient.invalidateQueries({ queryKey: ['document-records', property.id] });
      queryClient.invalidateQueries({ queryKey: ADMIN_PROPERTIES_QUERY_KEY });
      setRetryTargetRecord(null);
    } catch (err) {
      console.error('Address override failed:', err);
      setRetryError(err instanceof Error ? err.message : 'Retry failed.');
    } finally {
      setRetrySubmitting(false);
    }
  };

  // Cancel upload on the verify-document-address modal: discard the upload
  // entirely (S3 object + Document + every linked DocumentRecord). Falls back
  // to a per-record delete if the row predates documentId tracking.
  const handleCancelRetry = async () => {
    const r = retryTargetRecord?.record;
    if (!r) {
      setRetryTargetRecord(null);
      setRetryError(undefined);
      return;
    }
    setRetrySubmitting(true);
    setRetryError(undefined);
    try {
      if (r.documentId) {
        await cancelPendingUpload(r.documentId);
        showAlert('Upload cancelled — document removed from the system.', 'success');
      } else {
        await deleteDocumentRecord({ id: r.id });
        showAlert('Upload cancelled — pending record removed.', 'success');
      }
      queryClient.invalidateQueries({ queryKey: ['document-records', property.id] });
      queryClient.invalidateQueries({ queryKey: ADMIN_PROPERTIES_QUERY_KEY });
      setRetryTargetRecord(null);
    } catch (err) {
      console.error('Cancel pending upload failed:', err);
      // 409 = another reviewer already resolved this row. Refresh + dismiss
      // rather than showing the user a generic failure.
      if (isApiError(err) && err.response?.status === 409) {
        queryClient.invalidateQueries({ queryKey: ['document-records', property.id] });
        queryClient.invalidateQueries({ queryKey: ADMIN_PROPERTIES_QUERY_KEY });
        setRetryTargetRecord(null);
        showAlert('This upload was already resolved by another reviewer.', 'info');
      } else {
        setRetryError(err instanceof Error ? err.message : 'Cancel failed.');
      }
    } finally {
      setRetrySubmitting(false);
    }
  };

  // ── Assigned Deals state ──────────────────────────────────────────────────
  const [isDealAssigning, setIsDealAssigning] = useState(false);
  const [dealSearch, setDealSearch] = useState('');
  const [isDealPending, setIsDealPending] = useState(false);
  const dealInputRef = useRef<HTMLDivElement>(null);
  const [dealDropdownPos, setDealDropdownPos] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);

  useEffect(() => {
    if (isDealAssigning && dealInputRef.current) {
      const r = dealInputRef.current.getBoundingClientRect();
      setDealDropdownPos({ top: r.bottom + 4, left: r.left, width: r.width });
    } else {
      setDealDropdownPos(null);
    }
  }, [isDealAssigning]);

  const dealOptions = useMemo(() => {
    const opts: { dealId: string; groupName: string; dealName: string }[] = [];
    for (const g of rawGroups) {
      const groupName = g.holdingName || g.id;
      for (const d of g.deals ?? []) {
        if (d.status !== 'Closed') opts.push({ dealId: d.id, groupName, dealName: d.name });
      }
    }
    return opts;
  }, [rawGroups]);

  const dealMatches = useMemo(() => {
    const q = dealSearch.trim().toLowerCase();
    if (!q) return dealOptions.slice(0, 8);
    return dealOptions
      .filter((o) => o.groupName.toLowerCase().includes(q) || o.dealName.toLowerCase().includes(q))
      .slice(0, 8);
  }, [dealOptions, dealSearch]);

  const handleAddToDeal = async (dealId: string) => {
    setIsDealPending(true);
    try {
      await createListing(dealId, {
        address: property.address,
        propertyId: property.id,
        status: 'Touring',
      });
      await queryClient.invalidateQueries({ queryKey: CLIENT_GROUPS_QUERY_KEY });
      setIsDealAssigning(false);
      setDealSearch('');
    } catch (error) {
      console.error('Failed to add property to deal', error);
      showAlert(t('property.grant_failed'), 'error');
    } finally {
      setIsDealPending(false);
    }
  };

  const hoaToggle = (showLabel: boolean) => (
    <div
      onClick={(e) => e.stopPropagation()}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 5,
        justifyContent: showLabel ? 'flex-end' : 'flex-start',
        marginLeft: showLabel ? 'auto' : 0,
        textTransform: 'none',
        letterSpacing: 'normal',
        flexShrink: 0,
      }}>
      {showLabel && (
        <span style={{ fontSize: 12, fontWeight: 400, color: C.muted, fontFamily: font }}>HOA</span>
      )}
      <Switch
        checked={!!property.hasHOA}
        disabled={updateHasHOAMutation.isPending}
        onChange={(_, checked) =>
          updateHasHOAMutation.mutate({ propertyId: property.id, hasHOA: checked })
        }
        size="small"
      />
    </div>
  );

  // Collapsed, a property is a flat row. Open on a computer, the whole property (row and documents panel) lifts into
  // one raised box, the Clients & Deals treatment, so it reads as the item in focus. The box sits 8px in from the card
  // and the row's padding drops by the same 8px, so its columns stay on the header's
  const openBox = isOpen && !isMobile;
  return (
    <div
      style={
        openBox
          ? {
              margin: '8px 8px',
              borderRadius: '16px',
              background: 'var(--tx-glass-card)',
              backgroundImage: 'var(--tx-surface-light)',
              boxShadow: 'var(--tx-shadow-sm)',
            }
          : { ...(!isLast && !isOpen ? insetDivider : {}) }
      }>
      <div
        onClick={onToggle}
        style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '14px 1fr 110px' : isCompact ? MG_COMPACT : MG_FULL,
          padding: isMobile ? '14px 20px' : openBox ? '8px 12px' : '8px 20px',
          margin: 0,
          alignItems: 'center',
          justifyItems: 'stretch',
          gap: isMobile ? 8 : 24,
          cursor: 'pointer',
        }}>
        <div
          style={{
            transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s',
            transformOrigin: 'center',
            color: C.subtle,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
          }}>
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round">
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, overflow: 'hidden' }}>
            <span
              style={{
                fontSize: 13,
                fontWeight: 600,
                letterSpacing: '-0.01em',
                color: C.text,
                fontFamily: font,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                minWidth: 0,
              }}>
              {property.address}
            </span>
            <span
              onClick={(e) => {
                e.stopPropagation();
                onMapOpen(property);
              }}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                cursor: 'pointer',
                lineHeight: 0,
                flexShrink: 0,
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLElement).style.opacity = '0.7';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLElement).style.opacity = '1';
              }}>
              <svg
                width="11"
                height="11"
                viewBox="0 0 24 24"
                fill="none"
                stroke="var(--tx-faint)"
                strokeWidth="2"
                strokeLinecap="round"
                style={{ flexShrink: 0 }}>
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
                <circle cx="12" cy="10" r="3" />
              </svg>
            </span>
          </div>
          <div
            style={{
              fontSize: 11,
              color: C.muted,
              fontFamily: font,
              marginTop: 2,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}>
            {property.city}, {property.state} {property.zip}
          </div>
          {isMobile && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
              <ReportBadge
                hasReport={
                  property.hasReport ||
                  docRecords.some(
                    (r: any) =>
                      r.documentType && r.documentType !== DocumentClassificationType.PENDING,
                  )
                }
                count={
                  isOpen
                    ? docRecords.filter(
                        (r: any) =>
                          r.documentType && r.documentType !== DocumentClassificationType.PENDING,
                      ).length
                    : property.inspectionReportCount
                }
                mobileSize={12}
              />
              {assigned.length > 0 && (
                <span style={{ fontSize: 12, color: C.muted, fontFamily: font }}>
                  {assigned.length} client{assigned.length !== 1 ? 's' : ''}
                </span>
              )}
            </div>
          )}
        </div>
        {!isMobile && (
          <div
            style={{
              justifySelf: 'stretch',
              width: '100%',
              textAlign: 'right',
              fontSize: 13,
              fontWeight: 500,
              color: C.text,
              fontFamily: font,
              fontVariantNumeric: 'tabular-nums',
            }}>
            {property.price != null ? (
              `$${property.price.toLocaleString()}`
            ) : (
              <span style={{ color: C.subtle, fontStyle: 'italic' }}>—</span>
            )}
          </div>
        )}
        {!isMobile && (
          <div style={{ fontSize: 13, color: C.muted, fontFamily: font }}>
            {property.listedDate != null ? (
              formatLocalDate(epochSecondsToLocalDateStr(property.listedDate))
            ) : (
              <span style={{ color: C.subtle, fontStyle: 'italic' }}>—</span>
            )}
          </div>
        )}
        {!isMobile && (
          <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' as const }}>
            {assigned.length === 0 ? (
              property.dealStatus === 'Under Contract' ? (
                <UnderContractLockChip />
              ) : property.dealStatus === 'Pending' ||
                property.dealStatus === 'Escrow' ||
                property.dealStatus === 'Closed' ? (
                <DealStatusChip status={property.dealStatus} />
              ) : (
                <span style={{ color: C.subtle, fontStyle: 'italic' }}>—</span>
              )
            ) : (
              assigned.slice(0, 2).map((g) => <ClientChip key={g.id} group={g} />)
            )}
            {assigned.length > 2 && (
              <span
                style={{ fontSize: 11, color: C.subtle, fontFamily: font, alignSelf: 'center' }}>
                +{assigned.length - 2}
              </span>
            )}
          </div>
        )}
        {!isMobile && (
          <ReportBadge
            hasReport={
              property.hasReport ||
              docRecords.some(
                (r: any) => r.documentType && r.documentType !== DocumentClassificationType.PENDING,
              )
            }
            count={
              isOpen
                ? docRecords.filter(
                    (r: any) =>
                      r.documentType && r.documentType !== DocumentClassificationType.PENDING,
                  ).length
                : property.inspectionReportCount
            }
          />
        )}
        {!isMobile &&
          (canManageAccess ? (
            hoaToggle(false)
          ) : (
            <span
              style={{
                fontSize: 12,
                color: property.hasHOA ? C.muted : C.subtle,
                fontFamily: font,
              }}>
              {property.hasHOA ? 'Yes' : '—'}
            </span>
          ))}
        {!isMobile && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <svg
              width={14}
              height={14}
              viewBox="0 0 24 24"
              fill="none"
              stroke={interestCount > 0 ? C.muted : C.subtle}
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ flexShrink: 0 }}>
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
              <circle cx="12" cy="12" r="3" />
            </svg>
            <span
              style={{
                fontSize: 13,
                fontWeight: 500,
                color: interestCount > 0 ? C.text : C.subtle,
                fontFamily: font,
              }}>
              {interestCount}
            </span>
          </div>
        )}
        <div
          style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}
          onClick={(e) => e.stopPropagation()}>
          <Tooltip label="Open Dashboard">
            <button
              onClick={() => onOpen(property.id)}
              style={{
                ...btnGhost,
                width: isMobile || isCompact ? 32 : 'auto',
                height: isMobile || isCompact ? 32 : 'auto',
                borderRadius: 8,
                padding: isMobile || isCompact ? 0 : '5px 12px',
                fontSize: 11,
                gap: 5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                whiteSpace: 'nowrap',
              }}>
              <svg
                width="10"
                height="10"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round">
                <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6" />
                <polyline points="15 3 21 3 21 9" />
                <line x1="10" y1="14" x2="21" y2="3" />
              </svg>
              {!isCompact && <span>Open Dashboard</span>}
            </button>
          </Tooltip>
          {isAdmin && (
            <Tooltip label="Delete Property">
              <button
                onClick={() => onDelete(property.id)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 4,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke={L.critical}
                  strokeWidth="2"
                  strokeLinecap="round">
                  <polyline points="3 6 5 6 21 6" />
                  <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a1 1 0 011-1h4a1 1 0 011 1v2" />
                </svg>
              </button>
            </Tooltip>
          )}
        </div>
      </div>
      {isOpen && (
        <div
          style={{
            // flat inside the open box: no surface of its own
            paddingBottom: isMobile ? 20 : 12,
            margin: 0,
          }}>
          {/* Top row: Documents (left) + CTAs (right) */}
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 24,
              // hangs from the address column: the row's 12px padding + the 28px chevron column + its 24px gap
              padding: isMobile ? '12px 20px 0' : '12px 12px 0 64px',
              flexDirection: isMobile ? 'column' : 'row',
            }}>
            {/* Documents (left, under address) */}
            <div
              style={{
                minWidth: 0,
                flex: 1,
                overflow: 'hidden',
                width: isMobile ? '100%' : 'auto',
              }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  flexDirection: 'column',
                  marginBottom: 12,
                }}>
                <div style={{ display: 'flex', alignItems: 'center', minHeight: 30, gap: 10 }}>
                  <span
                    style={{
                      flexShrink: 0,
                      fontSize: 12,
                      fontWeight: 600,
                      color: 'var(--tx-muted)',
                      fontFamily: font,
                    }}>
                    Documents <span style={{ color: 'var(--tx-faint)', fontWeight: 500 }}>{classifiedDocumentRecords.length}</span>
                  </span>
                  {canManageAccess && isMobile && hoaToggle(true)}
                </div>
                <div
                  role="tablist"
                  aria-label="Property document groups"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 20,
                    marginTop: 8,
                    marginBottom: 14,
                    minWidth: 0,
                    overflowX: 'auto',
                    scrollbarWidth: 'none',
                  }}>
                  {documentTabs.map((tab) => {
                    const isActive = activeDocumentGroup === tab.id;
                    return (
                      <button
                        key={tab.id}
                        type="button"
                        role="tab"
                        aria-selected={isActive}
                        onClick={(event) => {
                          event.stopPropagation();
                          setActiveDocumentGroup(tab.id);
                        }}
                        style={{
                          appearance: 'none',
                          flexShrink: 0,
                          border: 0,
                          // tabs: 12px, muted at rest and ink when current, the count in faint (the Clients panel's tabs)
                          borderBottom: `2px solid ${isActive ? 'var(--tx-text)' : 'transparent'}`,
                          borderRadius: 0,
                          background: 'transparent',
                          color: isActive ? 'var(--tx-text)' : 'var(--tx-muted)',
                          cursor: 'pointer',
                          fontFamily: font,
                          fontSize: 12,
                          fontWeight: isActive ? 600 : 500,
                          lineHeight: 1,
                          padding: '6px 0 8px',
                          whiteSpace: 'nowrap',
                        }}>
                        {tab.label}{' '}
                        <span style={{ color: 'var(--tx-faint)', fontWeight: 500 }}>{tab.count}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
              {classifiedDocumentRecords.length === 0 ? (
                <div
                  style={{ fontSize: 12, color: C.subtle, fontFamily: font, fontStyle: 'italic' }}>
                  No documents uploaded
                </div>
              ) : (
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns:
                      activeDocumentGroup === 'all' && !isMobile
                        ? 'repeat(3, minmax(0, 1fr))'
                        : '1fr',
                    columnGap: 10,
                    rowGap: 20,
                  }}>
                  {visibleDocumentGroups.map((group) => (
                    <div key={group.id}>
                      {activeDocumentGroup === 'all' && (
                        <div
                          style={{
                            color: 'var(--tx-muted)',
                            fontFamily: font,
                            fontSize: 11,
                            fontWeight: 500,
                            marginBottom: 8,
                          }}>
                          {group.label}
                        </div>
                      )}
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {group.records.map((r: any, idx: number) => {
                          const dt = r.documentType as DocumentClassificationType;
                          const carFormAcronym = getCarFormAcronym(r.name);
                          const label = r.logicalForm
                            ? looksLikeRealAcronym(r.logicalForm.formKey)
                              ? r.logicalForm.formKey
                              : r.logicalForm.shortLabel
                            : carFormAcronym ??
                              (DOCUMENT_TYPE_LABELS[dt] ?? dt)
                                .replace(/^HOA\s+/, '')
                                .replace(' Inspection', '');
                          const isPending =
                            r.addressCheckStatus === AddressCheckStatus.NeedsConfirmation;
                          // Uploaded filenames often carry a packet-ordering prefix ("34. ")
                          // and extension that a Pulse-titled form never shows — strip both so
                          // the fallback tooltip reads like a title, not a raw filename.
                          const cleanFileName = (r.name as string | undefined)
                            ?.replace(/^\d+\.\s*/, '')
                            .replace(/\.[^.]+$/, '')
                            .trim();
                          const isBuyer =
                            r.source === 'buyer' ||
                            (import.meta.env.DEV &&
                              new URLSearchParams(window.location.search).get('mock') ===
                                'buyer-badge' &&
                              idx === 0);

                          const handleClick = async (e: React.MouseEvent) => {
                            e.stopPropagation();
                            if (isPending) {
                              // Open the address-mismatch dialog so the user can review
                              // the detected address, view the document, and override.
                              setRetryTargetRecord({ record: r, property });
                              return;
                            }
                            try {
                              const url = await generateDocumentRecordUrl({ id: r.id });
                              if (url) {
                                openInNewTab(withPdfPage(url, r.logicalForm?.primaryPage));
                              }
                            } catch {
                              /* silent */
                            }
                          };

                          return (
                            <div
                              key={r.displayKey ?? r.id}
                              onClick={handleClick}
                              title={
                                isPending
                                  ? 'Address verification pending — click to review and confirm'
                                  : r.logicalForm?.title ?? cleanFileName ?? 'Open document'
                              }
                              style={{
                                fontSize: 11,
                                fontWeight: 600,
                                fontFamily: font,
                                color: isPending ? '#1a1a1a' : 'var(--tx-text)',
                                // a square tag (Tradehaus .tx-pill): tonal fill, no outline; amber while it needs confirming
                                background: isPending ? '#f6c14a' : 'var(--tx-fill-2)',
                                border: 'none',
                                borderRadius: 6,
                                padding: '4px 8px',
                                cursor: 'pointer',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 4,
                              }}>
                              {isPending && (
                                <svg
                                  width="10"
                                  height="10"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round">
                                  <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                                  <line x1="12" y1="9" x2="12" y2="13" />
                                  <line x1="12" y1="17" x2="12.01" y2="17" />
                                </svg>
                              )}
                              {label}
                              {isBuyer && (
                                <span
                                  style={{
                                    fontSize: 10,
                                    fontWeight: 600,
                                    fontFamily: font,
                                    color: 'var(--tx-muted)',
                                    border: 0,
                                    background: 'var(--tx-fill)',
                                    borderRadius: 6,
                                    padding: '1px 5px',
                                    letterSpacing: '0.04em',
                                    marginLeft: 2,
                                  }}>
                                  BUYER
                                </span>
                              )}
                              {isPending && <span style={{ opacity: 0.85 }}>· Pending</span>}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {/* CTAs (right) — swaps to inline deal picker when linking a client */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 5,
                flexShrink: 0,
                flexWrap: 'nowrap',
                justifyContent: isMobile ? 'flex-end' : undefined,
                width: isMobile ? '100%' : 'auto',
              }}>
              {isDealAssigning ? (
                <div
                  style={{
                    display: 'flex',
                    gap: 8,
                    alignItems: 'center',
                    width: isMobile ? '100%' : 400,
                  }}
                  onClick={(e) => e.stopPropagation()}>
                  <div ref={dealInputRef} style={{ flex: 1, position: 'relative' }}>
                    <input
                      autoFocus
                      value={dealSearch}
                      onChange={(e) => setDealSearch(e.target.value)}
                      placeholder="Search by client or deal name…"
                      disabled={isDealPending}
                      style={{
                        width: '100%',
                        height: 32,
                        background: C.input,
                        border: 'none',
                        borderRadius: 8,
                        padding: '0 12px',
                        color: C.text,
                        fontSize: 12,
                        fontFamily: font,
                        outline: 'none',
                        boxSizing: 'border-box',
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = C.muted;
                      }}
                      onBlur={(e) => {
                        setTimeout(() => {
                          e.target.style.borderColor = C.border;
                        }, 150);
                      }}
                    />
                    {dealMatches.length > 0 &&
                      dealDropdownPos &&
                      createPortal(
                        <div
                          style={{
                            position: 'fixed',
                            top: dealDropdownPos.top,
                            left: dealDropdownPos.left,
                            width: dealDropdownPos.width,
                            zIndex: 9999,
                            background: C.card,
                            backgroundImage: 'var(--tx-surface-light)',
                            border: 'none',
                            borderRadius: '9px',
                            boxShadow: 'var(--tx-shadow)',
                            maxHeight: 280,
                            overflowY: 'auto',
                          }}>
                          {dealMatches.map((o) => (
                            <div
                              key={o.dealId}
                              onMouseDown={(e) => {
                                e.preventDefault();
                                handleAddToDeal(o.dealId);
                              }}
                              style={{
                                padding: '8px 12px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 10,
                                fontSize: 12,
                                fontFamily: font,
                                color: C.text,
                              }}
                              onMouseEnter={(e) => {
                                (e.currentTarget as HTMLElement).style.background =
                                  'rgba(255,255,255,0.06)';
                              }}
                              onMouseLeave={(e) => {
                                (e.currentTarget as HTMLElement).style.background = 'transparent';
                              }}>
                              <div
                                style={{
                                  fontWeight: 600,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}>
                                {o.groupName}
                                <span style={{ color: C.subtle, fontWeight: 400 }}> › </span>
                                {o.dealName}
                              </div>
                            </div>
                          ))}
                        </div>,
                        document.body,
                      )}
                  </div>
                  <button
                    onClick={() => {
                      setIsDealAssigning(false);
                      setDealSearch('');
                    }}
                    disabled={isDealPending}
                    style={{
                      background: 'transparent',
                      color: C.muted,
                      border: 'none',
                      fontSize: 11,
                      fontWeight: 600,
                      fontFamily: font,
                      cursor: 'pointer',
                      padding: '6px 8px',
                    }}>
                    Cancel
                  </button>
                </div>
              ) : (
                <>
                  <ScoringStatusChips propertyId={property.id} hauserEq={property.hauserEq} />
                  {canManageAccess && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsDealAssigning(true);
                      }}
                      style={{
                        background: 'var(--tx-fill)',
                        color: C.text,
                        border: 'none',
                        borderRadius: 8,
                        padding: isMobile ? '6px 10px' : '6px 14px',
                        fontSize: 12,
                        fontWeight: 600,
                        fontFamily: font,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 6,
                        flex: isMobile ? 1 : undefined,
                      }}>
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#c0ff02"
                        strokeWidth="2.5"
                        strokeLinecap="round">
                        <line x1="12" y1="5" x2="12" y2="19" />
                        <line x1="5" y1="12" x2="19" y2="12" />
                      </svg>
                      {isMobile ? 'Client' : 'Link Client'}
                    </button>
                  )}
                  {property.lat && property.lng && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onMapOpen(property);
                      }}
                      style={{
                        background: 'var(--tx-fill)',
                        color: C.text,
                        border: 'none',
                        borderRadius: 8,
                        padding: isMobile ? '6px 10px' : '6px 14px',
                        fontSize: 12,
                        fontWeight: 600,
                        fontFamily: font,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 6,
                        flex: isMobile ? 1 : undefined,
                      }}>
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round">
                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
                        <circle cx="12" cy="10" r="3" />
                      </svg>
                      Map
                    </button>
                  )}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onUpload(property);
                    }}
                    style={{
                      background: 'var(--tx-fill)',
                      color: C.text,
                      border: 'none',
                      borderRadius: 8,
                      padding: isMobile ? '6px 10px' : '6px 14px',
                      fontSize: 12,
                      fontWeight: 600,
                      fontFamily: font,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 6,
                      flex: isMobile ? 1 : undefined,
                    }}>
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round">
                      <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                      <polyline points="17 8 12 3 7 8" />
                      <line x1="12" y1="3" x2="12" y2="15" />
                    </svg>
                    Upload
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
      <AddressMismatchDialog
        open={!!retryTargetRecord}
        payload={
          retryTargetRecord
            ? {
                documentId: retryTargetRecord.record.documentId ?? '',
                documentRecordId: retryTargetRecord.record.id,
                documentName: retryTargetRecord.record.name,
                propertyId: retryTargetRecord.property.id,
                extractedAddress: retryTargetRecord.record.extractedAddress ?? null,
                propertyAddress: [
                  retryTargetRecord.property.address,
                  `${retryTargetRecord.property.city},`,
                  retryTargetRecord.property.state,
                  retryTargetRecord.property.zip,
                ]
                  .filter(Boolean)
                  .join(' '),
                addressConfidence: retryTargetRecord.record.extractedAddressConfidence ?? 0,
                confidenceThreshold: retryTargetRecord.record.confidenceThreshold ?? 0.5,
                reason: 'Pending approval — review and confirm to re-run.',
              }
            : undefined
        }
        isLoading={retrySubmitting}
        error={retryError}
        onConfirm={handleConfirmRetry}
        onCancel={handleCancelRetry}
        onDismiss={() => {
          // × / ESC — defer. Leave the pending row in place so another
          // reviewer can resolve it.
          setRetryTargetRecord(null);
          setRetryError(undefined);
        }}
      />
    </div>
  );
}

export const PropertiesPageNew = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isCompact = useMediaQuery(theme.breakpoints.down('lg'));
  const { showAlert } = useSnackbar();
  const { beginProcessingIndicator, resetProcessing } = useUploaderContext();
  const t = useTranslationPrefix('toasts');
  const { properties: contextProperties, setSelectedProperty } = usePropertiesContext();
  const { isAdmin, canViewAllProperties } = useUserRole();
  const { properties: adminProperties } = useAdminProperties({ enabled: canViewAllProperties });
  const { groups: rawGroups } = useClientGroups();
  const deleteMutation = useDeleteProperty();

  const [search, setSearch] = useState('');
  const [expandedIds, setExpandedIds] = useState(new Set<string>());
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState('asc');
  const [showActiveOnly, setShowActiveOnly] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  // the button the Add property panel drops down from (desktop); null opens the dialog (phones)
  const [addAnchor, setAddAnchor] = useState<HTMLElement | null>(null);
  const [dealPickerCtx, setDealPickerCtx] = useState<{
    propertyId: string;
    propertyAddress: string;
    groupId: string;
    buyerEmail?: string;
  } | null>(null);
  const [uploadTarget, setUploadTarget] = useState<NormalizedProperty | null>(null);
  const [mapTarget, setMapTarget] = useState<NormalizedProperty | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<NormalizedProperty | null>(null);

  // Build client groups from groups
  const clientGroups: ClientGroup[] = useMemo(
    () =>
      rawGroups.map((g) => {
        const member = g.members[0];
        const name = g.holdingName || member?.name || g.id;
        return { id: g.id, name, owner: name, userId: member?.userId ?? '' };
      }),
    [rawGroups],
  );

  // Property IDs with at least one active deal
  const activeDealsPropertyIds = useMemo(() => {
    const ids = new Set<string>();
    for (const g of rawGroups) {
      const hasActive = g.deals?.some((d: any) => d.status !== 'Closed') ?? false;
      if (hasActive) {
        for (const pid of g.propertyIds ?? []) ids.add(pid);
      }
    }
    return ids;
  }, [rawGroups]);

  // Build a reverse map: propertyId → groupIds from rawGroups
  const groupIdsByProperty = useMemo(() => {
    const map: Record<string, string[]> = {};
    for (const g of rawGroups) {
      for (const pid of g.propertyIds ?? []) {
        if (!map[pid]) map[pid] = [];
        map[pid].push(g.id);
      }
    }
    return map;
  }, [rawGroups]);

  // Normalize properties into a flat shape for the UI.
  // Admins and Advisors: use adminProperties from GET /properties/all — single source of truth
  //   for all properties in the system, already enriched with owner + report count.
  //   Used here only for KPI totals (KpiBar needs true counts, not one page's
  //   worth) — the actual list below is fed by searchedProperties instead.
  // Buyers: merge contextProperties (their own, with clientGroupIds) with any admin enrichment.
  const properties: NormalizedProperty[] = useMemo(() => {
    if (canViewAllProperties) {
      return adminProperties.map((p) => mapAdminProperty(p, groupIdsByProperty));
    }

    // Buyer view: contextProperties is source of truth, admin data enriches with report count
    const adminMap = new Map(adminProperties.map((p) => [p.id, p]));
    return (contextProperties || []).map((up) => {
      const admin = adminMap.get(up.property.id);
      return {
        id: up.property.id,
        address: up.property.address ?? '',
        city: up.property.city ?? '',
        state: up.property.state ?? '',
        zip: up.property.zipCode ?? '',
        createdAt: epochSecondsToLocalDateStr(up.property.createdAt),
        assignedGroups: up.clientGroupIds ?? [],
        hasReport: (admin?.inspectionReportCount ?? 0) > 0,
        inspectionReportCount: admin?.inspectionReportCount ?? 0,
        interestCount: admin?.interestCount ?? 0,
        price: admin?.price,
        listedDate: admin?.listedDate,
        lat: admin?.latitude,
        lng: admin?.longitude,
        hasHOA: admin?.hasHOA,
      };
    });
  }, [canViewAllProperties, contextProperties, adminProperties, groupIdsByProperty]);

  // Debounce the search box before it hits the backend — avoids firing a
  // request on every keystroke. 300ms is a standard search-input debounce.
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Admin/advisor property LIST (not KpiBar totals above) — real server-side
  // pagination + search via getAllProperties's `search` param, one page in
  // memory at a time instead of auto-fetching the whole table. See
  // useAdminPropertiesSearch's own comment for why this is a separate hook
  // from useAdminProperties.
  const {
    properties: searchedAdminProperties,
    hasNextPage: hasNextPropertiesPage,
    isFetchingNextPage: isFetchingNextPropertiesPage,
    fetchNextPage: fetchNextPropertiesPage,
  } = useAdminPropertiesSearch({ search: debouncedSearch, enabled: canViewAllProperties });

  const searchedProperties: NormalizedProperty[] = useMemo(
    () => searchedAdminProperties.map((p) => mapAdminProperty(p, groupIdsByProperty)),
    [searchedAdminProperties, groupIdsByProperty],
  );

  const toggleExpand = (id: string) =>
    setExpandedIds((prev) => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  const expandAll = () => setExpandedIds(new Set(filtered.map((p) => p.id)));
  const collapseAll = () => setExpandedIds(new Set());
  const handleSort = (key: string) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const sortableHdr = (label: string, key: string, align: 'left' | 'right' = 'left') => (
    <div
      onClick={() => handleSort(key)}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: align === 'right' ? 'flex-end' : 'flex-start',
        justifySelf: 'stretch',
        width: '100%',
        cursor: 'pointer',
        userSelect: 'none',
        color: C.muted,
      }}>
      <span
        style={{
          fontSize: 12,
          fontWeight: 600,
          fontFamily: font,
        }}>
        {label}
      </span>
      <SortCaret active={sortKey === key} dir={sortDir as 'asc' | 'desc'} />
    </div>
  );

  const handleOpen = (id: string) => {
    if (canViewAllProperties) {
      const adminProp = adminProperties.find((p) => p.id === id);
      if (adminProp) setSelectedProperty(adminPropertyToUserProperty(adminProp));
    } else {
      const userProperty = contextProperties?.find((up) => up.property.id === id);
      if (userProperty) setSelectedProperty(userProperty);
    }
    navigate('/dashboard/summary');
  };

  const handleDelete = (id: string) => {
    const target = properties.find((p) => p.id === id) ?? null;
    setDeleteTarget(target);
  };

  const confirmDelete = () => {
    if (!deleteTarget) return;
    deleteMutation.mutate(deleteTarget.id, {
      onSuccess: () => {
        showAlert(t('property.deleted'), 'success');
        setDeleteTarget(null);
      },
      onError: () => {
        showAlert(t('property.delete_failed'), 'error');
        setDeleteTarget(null);
      },
    });
  };

  const handleUpload = async (propertyId: string, files: UploadFile[]) => {
    // Bootstrap the sidebar processing indicator immediately so the user sees
    // feedback during the 5–30s window before the first SNS progress event
    // bumps stepProcessing through stages 2–4.
    beginProcessingIndicator();
    try {
      const presigned = await generatePreSignedUrls({
        documentUpload: true,
        files: files.map(({ file }) => ({
          filename: file.name,
          mimeType: file.type || 'application/octet-stream',
          fileSize: file.size,
          isExtractable: true,
        })),
      });
      if (presigned.length !== files.length) throw new Error('Unable to prepare every document.');

      const batch = await createDocumentUploadBatch({
        propertyId,
        expectedDocumentCount: files.length,
      });

      // Transfers may run together, but each successful transfer is registered
      // immediately. The state-machine lease serializes the expensive work per
      // property without making the user wait to upload an HOA packet.
      const outcomes = await Promise.all(
        files.map(async ({ file, source }, index) => {
          const fileKey = `${file.name}:${file.size}:${file.lastModified}`;
          const upload = presigned[index];
          try {
            const uploadResponse = await fetch(upload.preSignedUrl, {
              method: 'PUT',
              body: file,
              headers: { 'Content-Type': file.type || 'application/octet-stream' },
            });
            if (!uploadResponse.ok) throw new Error(`Unable to upload ${file.name}.`);
          } catch {
            await recordDocumentUploadBatchFailure(batch.id, {
              fileKey,
              s3Key: upload.key,
              stage: 'transfer',
            });
            return { succeeded: false, message: `Unable to upload "${file.name}".` };
          }

          try {
            await createDocuments({
              propertyId,
              source,
              uploadBatchId: batch.id,
              expectedDocumentCount: files.length,
              documents: [
                {
                  filename: file.name,
                  fileSize: file.size,
                  mimeType: file.type || 'application/octet-stream',
                  s3Key: upload.key,
                },
              ],
            });
            return { succeeded: true };
          } catch (error) {
            const reconciliation = await recordDocumentUploadBatchFailure(batch.id, {
              fileKey,
              s3Key: upload.key,
              stage: 'registration',
            });
            const message =
              isApiError<{ code?: string; message?: string }>(error) &&
              error.response?.data?.code === 'duplicate_file' &&
              typeof error.response.data.message === 'string'
                ? error.response.data.message
                : `Unable to register "${file.name}". Please try again.`;
            return { succeeded: reconciliation.registered === true, message };
          }
        }),
      );
      const succeeded = outcomes.filter((outcome) => outcome.succeeded).length;
      const failed = outcomes.length - succeeded;
      if (succeeded === 0) {
        const messages = outcomes.flatMap((outcome) =>
          'message' in outcome && outcome.message ? [outcome.message] : [],
        );
        throw new Error(messages.join(' ') || 'No documents could be uploaded.');
      }
      // Refresh data — document-records query so the new file appears immediately
      queryClient.invalidateQueries({ queryKey: ADMIN_PROPERTIES_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: ['properties'] });
      queryClient.invalidateQueries({ queryKey: ['document-records', propertyId] });
      showAlert(
        failed
          ? `${succeeded} of ${files.length} documents queued; ${failed} failed to upload`
          : files.length === 1
          ? t('report.uploaded')
          : `${files.length} documents queued for processing`,
        failed ? 'warning' : 'success',
      );
    } catch (error) {
      resetProcessing();
      showAlert(t('report.upload_failed'), 'error');
      throw error;
    }
  };

  // Get live version of upload target (so modal reflects latest data)
  const liveUploadProp = uploadTarget
    ? properties.find((p) => p.id === uploadTarget.id) ?? uploadTarget
    : null;

  // Admin/advisor: search already happened server-side (searchedProperties
  // is the result of it) — only the active-only toggle applies here, on top
  // of whatever page(s) are currently loaded. Buyer view has no pagination
  // (a buyer's own property list is small), so it keeps client-side text
  // filtering over the full list.
  const filtered = (canViewAllProperties ? searchedProperties : properties).filter((p) => {
    if (showActiveOnly && !activeDealsPropertyIds.has(p.id)) return false;
    if (canViewAllProperties) return true;
    const q = search.toLowerCase();
    return (
      !q ||
      p.address.toLowerCase().includes(q) ||
      p.city.toLowerCase().includes(q) ||
      p.zip.includes(q)
    );
  });

  // Strip leading street number (e.g. "123 ", "10B ") so addresses sort by street name
  const streetName = (address: string) => address.replace(/^\d+[A-Za-z]?\s+/, '').toLowerCase();

  const sorted = [...filtered].sort((a, b) => {
    if (!sortKey) {
      // Default: alphabetical by street name
      const an = streetName(a.address);
      const bn = streetName(b.address);
      if (an < bn) return -1;
      if (an > bn) return 1;
      return 0;
    }
    let av: any, bv: any;
    if (sortKey === 'address') {
      av = streetName(a.address);
      bv = streetName(b.address);
    } else if (sortKey === 'city') {
      av = a.city.toLowerCase();
      bv = b.city.toLowerCase();
    } else if (sortKey === 'interest') {
      av = a.interestCount;
      bv = b.interestCount;
    } else if (sortKey === 'price') {
      av = a.price ?? -1;
      bv = b.price ?? -1;
    } else if (sortKey === 'listedDate') {
      av = a.listedDate ?? -1;
      bv = b.listedDate ?? -1;
    }
    if (av < bv) return sortDir === 'asc' ? -1 : 1;
    if (av > bv) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  const allExpanded = sorted.length > 0 && sorted.every((p) => expandedIds.has(p.id));

  return (
    <div
      style={{
        height: '100dvh',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        background: 'transparent',
        fontFamily: font,
        // phone: the frame already sets the gap under the menu bar
        padding: isMobile ? '0 10px 20px' : '20px 0',
        scrollbarWidth: 'none' as const,
      }}>
      <style>{`
        input::placeholder{color:#6b6b6b;opacity:1;}
        @keyframes uc-glow-pulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(255,249,129,0.55); }
          50% { box-shadow: 0 0 0 5px rgba(255,249,129,0); }
        }
      `}</style>

      {/* Real AddPropertyModal — handles address lookup + createProperty API */}
      <AddPropertyModal open={showAdd} anchorEl={addAnchor} onClose={() => setShowAdd(false)} />

      {liveUploadProp && (
        <UploadReportModal
          property={liveUploadProp}
          onClose={() => setUploadTarget(null)}
          onUpload={handleUpload}
        />
      )}
      {dealPickerCtx && (
        <DealPickerModal
          open
          propertyAddress={dealPickerCtx.propertyAddress}
          propertyId={dealPickerCtx.propertyId}
          groupId={dealPickerCtx.groupId}
          buyerEmail={dealPickerCtx.buyerEmail}
          onClose={() => setDealPickerCtx(null)}
          onDone={() => {
            setDealPickerCtx(null);
            queryClient.invalidateQueries({ queryKey: CLIENT_GROUPS_QUERY_KEY });
          }}
        />
      )}

      <div
        style={{
          width: '100%',
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}>
        <KpiBar properties={properties} />

        <div
          style={{
            background: C.card,
            backgroundImage: 'var(--tx-surface-light)',
            boxShadow: 'var(--tx-shadow)',
            borderRadius: '24px',
            paddingBottom: 10,
            maxWidth: '100%',
            boxSizing: 'border-box',
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}>
          {/* Toolbar: the view filter left; search, add and icon actions right (the Clients & Deals toolbar) */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 10,
              padding: 20,
            }}>
            {!isMobile && (
              <Segmented
                label="Properties shown"
                options={[
                  ['active', 'Active'],
                  ['all', 'All'],
                ]}
                value={showActiveOnly ? 'active' : 'all'}
                onChange={(v) => setShowActiveOnly(v === 'active')}
              />
            )}
            {!isMobile && <div style={{ flex: 1 }} />}
            <TextInput
              icon="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search address, city, ZIP…"
              sx={{ flex: isMobile ? 1 : 'none', width: isMobile ? undefined : 240 }}
            />
            {isMobile ? (
              <DsIconButton icon="plus" label="Add property" onClick={() => setShowAdd(true)} />
            ) : (
              <DsButton
                variant="add"
                onClick={(e) => {
                  setAddAnchor(e.currentTarget);
                  setShowAdd(true);
                }}>
                Add property
              </DsButton>
            )}
            <DsIconButton
              label={allExpanded ? 'Collapse all' : 'Expand all'}
              onClick={allExpanded ? collapseAll : expandAll}
              tipPlacement="bottom"
              icon={
                <svg
                                width="15"
                                height="15"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round">
                                {allExpanded ? (
                                  <>
                                    <polyline points="4 14 10 14 10 20" />
                                    <polyline points="20 10 14 10 14 4" />
                                    <line x1="10" y1="14" x2="3" y2="21" />
                                    <line x1="21" y1="3" x2="14" y2="10" />
                                  </>
                                ) : (
                                  <>
                                    <polyline points="15 3 21 3 21 9" />
                                    <polyline points="9 21 3 21 3 15" />
                                    <line x1="21" y1="3" x2="14" y2="10" />
                                    <line x1="3" y1="21" x2="10" y2="14" />
                                  </>
                                )}
                              </svg>
              }
            />
          </div>
          {isMobile && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                padding: '0 20px 12px',
              }}>
              <div
                onClick={() => setShowActiveOnly((p) => !p)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  cursor: 'pointer',
                  userSelect: 'none',
                }}>
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 500,
                    fontFamily: font,
                    color: showActiveOnly ? 'var(--tx-lime-ink)' : C.subtle,
                  }}>
                  Active
                </span>
                <div
                  style={{
                    width: 36,
                    height: 20,
                    borderRadius: 10,
                    background: showActiveOnly ? 'var(--tx-lime)' : 'var(--tx-fill)',
                    border: 0,
                    position: 'relative',
                    transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                    boxShadow: showActiveOnly ? '0 0 8px rgba(192,255,2,0.3)' : 'none',
                  }}>
                  <div
                    style={{
                      width: 14,
                      height: 14,
                      borderRadius: '50%',
                      background: showActiveOnly ? 'var(--tx-glass-panel)' : C.subtle,
                      position: 'absolute',
                      top: 2,
                      left: showActiveOnly ? 19 : 2,
                      transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                    }}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Header row: outside the scroll so it needs no fill of its own, only the hairline under the names. The
              scrollbar is hidden, so the header and rows share one width and their columns line up */}
          {!isMobile && (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: isCompact ? MG_COMPACT : MG_FULL,
                padding: '10px 20px 15px',
                ...insetDivider,
                gap: 24,
                justifyItems: 'stretch',
              }}>
              <div />
              {sortableHdr('Address', 'address')}
              {sortableHdr('Price', 'price', 'right')}
              {sortableHdr('Listed', 'listedDate')}
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: 'var(--tx-muted)',
                  fontFamily: font,
                  display: 'flex',
                  alignItems: 'center',
                  width: '100%',
                }}>
                Clients
              </div>
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: 'var(--tx-muted)',
                  fontFamily: font,
                  display: 'flex',
                  alignItems: 'center',
                  width: '100%',
                }}>
                Docs
              </div>
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: 'var(--tx-muted)',
                  fontFamily: font,
                  display: 'flex',
                  alignItems: 'center',
                  width: '100%',
                }}>
                HOA
              </div>
              {sortableHdr('Interest', 'interest')}
              <div />
            </div>
          )}
          <div style={{ flex: 1, minHeight: 0, overflow: 'auto', scrollbarWidth: 'none' }}>
            {sorted.length === 0 ? (
              <div
                style={{
                  textAlign: 'center',
                  padding: '48px 0',
                  color: C.subtle,
                  fontSize: 14,
                  fontFamily: font,
                }}>
                No properties found.
              </div>
            ) : (
              sorted.map((p, i) => (
                <PropertyRow
                  key={p.id}
                  property={p}
                  clientGroups={clientGroups}
                  rawGroups={rawGroups}
                  isOpen={expandedIds.has(p.id)}
                  isLast={i === sorted.length - 1}
                  onToggle={() => toggleExpand(p.id)}
                  onUpload={(prop) => setUploadTarget(prop)}
                  onDelete={handleDelete}
                  onMapOpen={(prop) => setMapTarget(prop)}
                  onOpen={handleOpen}
                  isAdmin={isAdmin}
                />
              ))
            )}
            {canViewAllProperties && hasNextPropertiesPage && (
              <div style={{ textAlign: 'center', padding: '16px 0' }}>
                <button
                  type="button"
                  onClick={() => fetchNextPropertiesPage()}
                  disabled={isFetchingNextPropertiesPage}
                  style={{
                    background: 'var(--tx-fill)',
                    border: 0,
                    borderRadius: 6,
                    padding: '8px 16px',
                    color: C.text,
                    fontFamily: font,
                    fontSize: 13,
                    cursor: isFetchingNextPropertiesPage ? 'default' : 'pointer',
                    opacity: isFetchingNextPropertiesPage ? 0.6 : 1,
                  }}>
                  {isFetchingNextPropertiesPage ? 'Loading…' : 'Load more'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {mapTarget && <PropertyMapModal property={mapTarget} onClose={() => setMapTarget(null)} />}

      {deleteTarget && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 1300,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'var(--tx-backdrop)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
          }}
          onClick={() => setDeleteTarget(null)}>
          <div
            style={{
              background: 'var(--tx-glass-panel)',
              backgroundImage: 'var(--tx-surface-light)',
              boxShadow: 'var(--tx-shadow)',
              borderRadius: '24px',
              padding: '28px 28px 24px',
              width: '100%',
              maxWidth: 400,
            }}
            onClick={(e) => e.stopPropagation()}>
            <div
              style={{
                fontSize: 17,
                fontWeight: 600,
                color: 'var(--tx-text)',
                fontFamily: "'Manrope', sans-serif",
                marginBottom: 8,
              }}>
              Delete property
            </div>
            <div
              style={{
                fontSize: 13,
                color: 'var(--tx-muted)',
                fontFamily: "'Manrope', sans-serif",
                marginBottom: 24,
                lineHeight: 1.5,
              }}>
              Are you sure you want to delete{' '}
              <strong style={{ color: 'var(--tx-text)' }}>{deleteTarget.address}</strong>? This action
              cannot be undone.
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setDeleteTarget(null)}
                style={{
                  background: 'var(--tx-fill)',
                  border: 0,
                  borderRadius: 8,
                  padding: '8px 18px',
                  fontSize: 13,
                  fontWeight: 600,
                  fontFamily: "'Manrope', sans-serif",
                  color: 'var(--tx-muted)',
                  cursor: 'pointer',
                }}>
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={deleteMutation.isPending}
                style={{
                  background: 'rgba(219,82,130,0.15)',
                  border: 0,
                  borderRadius: 8,
                  padding: '8px 18px',
                  fontSize: 13,
                  fontWeight: 600,
                  fontFamily: "'Manrope', sans-serif",
                  color: '#DB5282',
                  cursor: deleteMutation.isPending ? 'not-allowed' : 'pointer',
                }}>
                {deleteMutation.isPending ? 'Deleting…' : 'Delete Property'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
